import React, { useContext, useState, useEffect } from 'react';
import { ShopContext, resolveProductImage, getCategoryFallbackImage, getProductImage } from '../context/ShopContext';
import { StarIcon, HeartIcon } from './Icons';
import { formatINR } from '../utils/currency';
import './ProductCard.css';

const ProductCard = ({ product }) => {
  const { setView, addToCart, wishlist, toggleWishlist } = useContext(ShopContext);

  const fallbackMain = getCategoryFallbackImage(product?.category);
  const initialImg = resolveProductImage(product);

  const resolveHoverImg = (prod) => {
    if (!prod || !Array.isArray(prod.images) || prod.images.length < 2) return null;
    const item = prod.images[1];
    let raw = typeof item === 'object' ? (item.preview || item.url || item.filePath) : item;
    if (typeof raw === 'string' && raw.trim().length > 0) {
      return getProductImage(raw, prod.category);
    }
    return null;
  };

  const [imgSrc, setImgSrc] = useState(initialImg);
  const [hoverImgSrc, setHoverImgSrc] = useState(resolveHoverImg(product));
  const [addedAnim, setAddedAnim] = useState(false);
  const [wishlistAnim, setWishlistAnim] = useState(false);

  useEffect(() => {
    const resolved = resolveProductImage(product);
    setImgSrc(resolved);
    setHoverImgSrc(resolveHoverImg(product));
  }, [product, fallbackMain]);

  const handleMainImgError = () => {
    if (imgSrc !== fallbackMain) {
      setImgSrc(fallbackMain);
    }
  };

  const handleHoverImgError = () => {
    setHoverImgSrc(null);
  };

  const getBadgeClass = (tag) => {
    if (tag === 'Best Seller' || tag === 'Sale') return 'badge-primary';
    if (tag === 'Trending' || tag === 'New Drop') return 'badge-secondary';
    return 'badge-secondary';
  };

  const handleQuickAdd = (e) => {
    e.stopPropagation();
    if (product.stock > 0) {
      addToCart(product, (product.sizes && product.sizes[0]) || 'M', (product.colors && product.colors[0]) || 'Black', 1);
      setAddedAnim(true);
      setTimeout(() => setAddedAnim(false), 1200);
    }
  };

  return (
    <div className="product-card luxury-card" onClick={() => setView('product-detail', product)}>
      <div className="product-card-img-container">
        {/* Tag Badge */}
        {(product.tag || product.discountedFrom) && (
          <span className={`badge ${getBadgeClass(product.tag || 'Sale')} product-card-badge`}>
            {product.tag || 'Sale'}
          </span>
        )}

        {/* Wishlist Button */}
        <button
          type="button"
          className={`product-card-wishlist-btn ${wishlist && wishlist.includes(product.id) ? 'in-wishlist' : ''} ${wishlistAnim ? 'pop-anim' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (toggleWishlist) toggleWishlist(product.id);
            setWishlistAnim(true);
            setTimeout(() => setWishlistAnim(false), 450);
          }}
          title={wishlist && wishlist.includes(product.id) ? "Remove from Wishlist" : "Add to Wishlist"}
          aria-label={wishlist && wishlist.includes(product.id) ? "Remove from Wishlist" : "Add to Wishlist"}
        >
          <HeartIcon 
            size={18} 
            fill={wishlist && wishlist.includes(product.id) ? "#ef4444" : "none"} 
            stroke={wishlist && wishlist.includes(product.id) ? "#ef4444" : "currentColor"} 
          />
        </button>

        {/* Main High-Res Image - 100% Crystal Clear & Immediate */}
        <img
          src={imgSrc}
          alt={product.name}
          className="product-card-img"
          loading="eager"
          decoding="async"
          fetchpriority="high"
          width="400"
          height="533"
          onError={handleMainImgError}
        />

        {/* Hover Alternate Angle Image (if exists) */}
        {hoverImgSrc && (
          <img
            src={hoverImgSrc}
            alt={product.name}
            className="product-card-img product-card-img-hover"
            loading="lazy"
            decoding="async"
            width="400"
            height="533"
            onError={handleHoverImgError}
          />
        )}

        {/* Action Overlay */}
        <div className="product-card-overlay">
          <button className="btn btn-secondary quick-view-btn">
            Quick Details
          </button>
          <button
            className={`btn btn-primary quick-add-btn ${addedAnim ? 'added-success' : ''}`}
            onClick={handleQuickAdd}
            disabled={product.stock === 0}
          >
            {addedAnim ? '✓ Added!' : product.stock > 0 ? 'Quick Add' : 'Sold Out'}
          </button>
        </div>
      </div>

      <div className="product-card-content">
        <div className="product-card-category-rating">
          <span className="product-card-category">{product.category}</span>
          <div className="product-card-rating">
            <StarIcon size={12} fill="currentColor" />
            <span>{product.rating || 5.0}</span>
            <span className="reviews-count">({product.reviewsCount || 42})</span>
          </div>
        </div>

        <h4 className="product-card-title" title={product.name}>
          {product.name}
        </h4>

        {/* Swatches Preview */}
        <div className="product-card-swatches">
          {product.colors && product.colors.slice(0, 5).map((color, index) => (
            <span
              key={index}
              className="color-swatch-dot"
              style={{
                backgroundColor: color.toLowerCase() === 'white' ? '#f5f5f5' : color.toLowerCase()
              }}
              title={color}
            />
          ))}
        </div>

        <div className="product-card-meta">
          <div className="price-wrapper">
            <span className="product-card-price">{formatINR(product.price)}</span>
            {product.discountedFrom && (
              <span className="product-card-original-price">{formatINR(product.discountedFrom)}</span>
            )}
          </div>
          <span className={`stock-status ${product.stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
            {product.stock > 0 ? `${product.stock} Left` : 'Sold Out'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProductCard);
